import { FormEvent, useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import {
  EmploymentType,
  Instructor,
  InstructorInput,
  createInstructor,
  setInstructorActive,
  updateInstructor,
} from "../../api/instructors";
import {
  extractPhoneDigits,
  formatPhoneDisplay,
  formatPhoneInput,
  normalizePhoneForApi,
  validateTurkishPhone,
} from "../../utils/phone";
import "./InstructorFormModal.css";

const WEEKDAY_PILLS: { index: number; label: string }[] = [
  { index: 0, label: "PAZARTESİ" },
  { index: 1, label: "SALI" },
  { index: 2, label: "ÇARŞAMBA" },
  { index: 3, label: "PERŞEMBE" },
  { index: 4, label: "CUMA" },
  { index: 5, label: "CUMARTESİ" },
  { index: 6, label: "PAZAR" },
];

type Props = {
  instructor: Instructor | null;
  onClose: () => void;
  onSaved: (instructor: Instructor) => void;
};

function emptyInput(): InstructorInput {
  return {
    fullName: "",
    phone: "",
    email: "",
    certificateExpiryDate: "",
    employmentType: "full_time",
    notes: "",
    isActive: true,
  };
}

function fromInstructor(instructor: Instructor): InstructorInput {
  return {
    fullName: instructor.full_name,
    phone: formatPhoneDisplay(instructor.phone),
    email: instructor.email || "",
    certificateExpiryDate: instructor.certificate_expiry_date
      ? instructor.certificate_expiry_date.slice(0, 10)
      : "",
    employmentType: instructor.employment_type,
    notes: instructor.notes || "",
    isActive: instructor.is_active,
    weeklyLeaveWeekday: instructor.weekly_leave_weekday ?? null,
  };
}

function cleanForApi(input: InstructorInput): InstructorInput {
  const trim = (v: string | null | undefined) => (typeof v === "string" ? v.trim() : v);
  const optional = (v: string | null | undefined) => {
    const trimmed = trim(v);
    return trimmed && trimmed.length > 0 ? trimmed : null;
  };
  return {
    fullName: (trim(input.fullName) || "") as string,
    phone: optional(input.phone) ? normalizePhoneForApi(optional(input.phone)!) : null,
    email: optional(input.email),
    certificateExpiryDate: optional(input.certificateExpiryDate),
    employmentType: input.employmentType,
    notes: optional(input.notes),
    isActive: input.isActive,
    weeklyLeaveWeekday: input.weeklyLeaveWeekday ?? null,
  };
}

export function InstructorFormModal({ instructor, onClose, onSaved }: Props) {
  const [form, setForm] = useState<InstructorInput>(() =>
    instructor ? fromInstructor(instructor) : emptyInput(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(instructor ? fromInstructor(instructor) : emptyInput());
    setError(null);
  }, [instructor]);

  const isEdit = Boolean(instructor);

  const update = <K extends keyof InstructorInput>(key: K, value: InstructorInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    const payload = cleanForApi(form);
    if (!payload.fullName) {
      setError("Ad soyad Zorunludur.");
      return;
    }
    const phoneErr = validateTurkishPhone(extractPhoneDigits(form.phone || ""));
    if (phoneErr) {
      setError(phoneErr);
      return;
    }
    setSubmitting(true);
    try {
      let saved: Instructor;
      if (isEdit) {
        saved = await updateInstructor(instructor!.id, payload);
        if (payload.isActive !== undefined && payload.isActive !== instructor!.is_active) {
          saved = await setInstructorActive(instructor!.id, payload.isActive);
        }
      } else {
        saved = await createInstructor(payload);
      }
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Kayit Başarısız.");
      } else {
        setError("Sunucuya ulasilamadi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-instructor-form">
        <div className="kp-modal__head">
          <div>
            <strong>{isEdit ? "Eğitmen Düzenle" : "Yeni Eğitmen"}</strong>
            <span>{isEdit ? instructor!.full_name : "Yeni kayit olusturun"}</span>
          </div>
          <button className="kp-btn kp-btn--ghost" onClick={onClose} disabled={submitting}>
            Kapat
          </button>
        </div>

        <form className="kp-instructor-form__grid" onSubmit={submit} autoComplete="off">
          <label className="kp-instructor-form__field kp-instructor-form__field--full">
            <span>Ad Soyad *</span>
            <input
              className="kp-input"
              type="text"
              required
              maxLength={150}
              value={form.fullName}
              onChange={(e) => update("fullName", e.target.value)}
            />
          </label>

          <label className="kp-instructor-form__field">
            <span>Telefon</span>
            <input
              className="kp-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              maxLength={16}
              value={form.phone || ""}
              onChange={(e) => update("phone", formatPhoneInput(e.target.value))}
              onBlur={() => {
                const digits = extractPhoneDigits(form.phone || "");
                if (digits) update("phone", formatPhoneDisplay(digits));
              }}
              placeholder="0555 685 75 80"
            />
          </label>

          <label className="kp-instructor-form__field">
            <span>E-posta</span>
            <input
              className="kp-input"
              type="email"
              maxLength={150}
              value={form.email || ""}
              onChange={(e) => update("email", e.target.value)}
            />
          </label>

          <label className="kp-instructor-form__field">
            <span>Sertifika Bitis Tarihi</span>
            <input
              className="kp-input"
              type="date"
              value={form.certificateExpiryDate || ""}
              onChange={(e) => update("certificateExpiryDate", e.target.value)}
            />
          </label>

          <label className="kp-instructor-form__field">
            <span>Çalışma Turu *</span>
            <select
              className="kp-input"
              value={form.employmentType}
              onChange={(e) => update("employmentType", e.target.value as EmploymentType)}
            >
              <option value="full_time">Tam zamanli (full_time)</option>
              <option value="daily_paid">Gunluk ucretli (daily_paid)</option>
            </select>
          </label>

          <label className="kp-instructor-form__field kp-instructor-form__field--full">
            <span>Notlar</span>
            <textarea
              className="kp-input"
              rows={3}
              maxLength={2000}
              value={form.notes || ""}
              onChange={(e) => update("notes", e.target.value)}
            />
          </label>

          {isEdit && (
            <div className="kp-instructor-form__field kp-instructor-form__field--full kp-instructor-form__weekly">
              <span className="kp-instructor-form__weekly-label">HAFTALIK İZİN GÜNÜ</span>
              <div className="kp-instructor-form__weekdays" role="group" aria-label="Haftalık izin günü">
                {WEEKDAY_PILLS.map((day) => (
                  <button
                    key={day.index}
                    type="button"
                    className={`kp-instructor-form__weekday${form.weeklyLeaveWeekday === day.index ? " kp-instructor-form__weekday--active" : ""}`}
                    onClick={() =>
                      update(
                        "weeklyLeaveWeekday",
                        form.weeklyLeaveWeekday === day.index ? null : day.index,
                      )
                    }
                    aria-pressed={form.weeklyLeaveWeekday === day.index}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              <small className="kp-instructor-form__status-hint">
                Yalnızca bugünden sonraki günlerde geçerlidir. Gelecekte bu günde planlı ders varsa kayıt
                yapılamaz.
              </small>
            </div>
          )}

          {isEdit && (
            <div className="kp-instructor-form__field kp-instructor-form__field--full kp-instructor-form__status">
              <span>Durum</span>
              <label className="kp-instructor-form__status-toggle">
                <input
                  type="checkbox"
                  checked={form.isActive !== false}
                  onChange={(e) => update("isActive", e.target.checked)}
                />
                <span>{form.isActive !== false ? "Aktif egitmen" : "Pasif egitmen"}</span>
              </label>
              <small className="kp-instructor-form__status-hint">
                Pasif egitmenler yeni randevu planlamada onerilmez; mevcut kayitlar korunur.
              </small>
            </div>
          )}

          {error && <div className="kp-instructor-form__error">{error}</div>}

          <div className="kp-instructor-form__actions">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              İptal
            </button>
            <button type="submit" className="kp-btn kp-btn--primary" disabled={submitting}>
              {submitting ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
